<template>
    <form
        class="flex flex-col gap-y-4"
        @submit="handleSubmitCheckoutForm"
    >
        <customer-information
            v-model:editing="editingCustomerInformation"
            :customer="primaryCustomer"
            :facility="facility"
            :show-errors="submitCount > 0"
        />

        <checkout-section-separator />

        <billing-address
            v-model:editing="editingBillingAddress"
            :customer="primaryCustomer"
            :is-full-address="fullAddressRequired"
            :title="t('checkout.billingAddress')"
        />

        <checkout-section-separator
            v-if="requiresRecurringToken && readyForRecurringToken"
        />

        <recurring-information
            v-if="requiresRecurringToken && readyForRecurringToken"
            ref="recurringInformationRef"
            v-model:editing="editingRecurringInformation"
            :customer="primaryCustomer"
            :facility="facility"
            :recurring-total="recurringTotal"
            :payment-method-input="paymentMethodInput"
        />

        <checkout-section-separator v-if="readyForPayment && requiresPayment" />

        <div v-else-if="!readyForPayment">
            <prime-button
                :label="
                    requiresPayment
                        ? t('checkout.continueToPayment')
                        : t('checkout.continueToTermsAndConditions')
                "
                type="submit"
                class="w-full"
                :loading="isValidating"
            />
        </div>

        <payment-information
            v-if="readyForPayment && requiresPayment"
            ref="paymentInformationRef"
            :customer="primaryCustomer"
            :facility="facility"
            :checkout-total="dueToday"
            :title="paymentTitle"
            :payment-method-input="paymentMethodInput"
        />

        <document-section
            v-if="readyForPayment"
            :contract="contract"
            :is-signing-document="isPending || isValidating"
            :balance-due-mode="false"
            :invalid-initials="invalidInitials"
            :invalid-signatures="invalidSignatures"
            @inline-checkout-document="handleInlineCheckoutDocument"
            @inline-checkout="handleInlineCheckout"
        />

        <div
            v-if="showPleaseFixMessage"
            class="text-red-600 mt-4 font-medium"
        >
            {{ t('checkout.pleaseFixErrorsInRedAbove') }}
        </div>
    </form>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/yup'
import { object } from 'yup'
import { useForm } from 'vee-validate'
import { useRpQuery, UserReportableError } from '~/composables/graphql'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { useSelectedLanguage } from '~/composables/use-selected-language'
import type {
    CheckoutRecurringInput,
    CommitInlineCheckoutInput,
    DocumentInput
} from '~/resources/graphql/graphql'
import { computed, nextTick, onMounted, ref, toRef, watch } from 'vue'
import { type FragmentType, graphql } from '~/resources/graphql'
import PrimeButton from 'primevue/button'
import { useActiveFacility, useShowAlert } from '#imports'
import { handleValidationErrors } from '~/components/PurchaseContract/DocumentSection/handle-validation-errors'

import {
    CheckoutFormCheckoutFragment,
    CheckoutFormQueryFragment,
    useCheckoutForm
} from '~/components/Checkout/Forms/use-checkout-form'

import CustomerInformation, {
    CustomerInformationCheckoutFormInitialValues,
    CustomerInformationCheckoutFormSchema
} from '~/components/Checkout/Sections/CustomerInformation.vue'
import BillingAddress, {
    BillingAddressInitialValues,
    BillingAddressSchema
} from '~/components/Checkout/Sections/BillingAddress.vue'
import RecurringInformation, {
    RecurringInformationInitialValues,
    RecurringInformationSchema
} from '~/components/Checkout/Sections/RecurringInformation.vue'
import PaymentInformation, {
    PaymentInformationInitialValues,
    PaymentInformationSchema,
    type PaymentInformationSchemaType
} from '~/components/Checkout/Sections/PaymentInformation.vue'
import DocumentSection, {
    DocumentSectionContractFragment
} from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import { GraphQLRequestError } from 'awesome-graphql-client'
import CheckoutSectionSeparator from '~/components/Checkout/Forms/CheckoutSectionSeparator.vue'
import {
    checkoutPaymentInput,
    checkoutRecurringInput,
    resetPaymentErrors
} from '~/components/Checkout/Forms/checkout-helpers'
import { CheckoutSuccessKioskDisplayFragment } from '~/components/Checkout/CheckoutSuccessKioskDisplay.vue'
import { usePaymentMethodInput } from '~/components/Checkout/Sections/use-payment-method-input'
import { useI18n } from 'vue-i18n'
import { PaymentSubmissionResult } from '~/components/Checkout/Sections/Payment/payment-method'

const props = defineProps<{
    query: FragmentType<typeof CheckoutFormQueryFragment>
    checkout: FragmentType<typeof CheckoutFormCheckoutFragment>
    contract: FragmentType<typeof DocumentSectionContractFragment>
}>()

const emit = defineEmits<{
    (
        e: 'kioskCheckoutSuccess',
        payload: FragmentType<typeof CheckoutSuccessKioskDisplayFragment>
    ): void
    (e: 'checkoutStatusChanged'): void
}>()

const { t } = useI18n({ useScope: 'global' })

const {
    checkoutId,

    primaryCustomer,
    facility,

    fullAddressRequired,
    requiresCustomerInformation,

    editingCustomerInformation,
    editingBillingAddress,
    editingRecurringInformation,

    dueToday,
    recurringTotal,
    requiresRecurringToken,

    incrementProcessing,
    decrementProcessing
} = useCheckoutForm(toRef(props, 'query'), toRef(props, 'checkout'))

const schema = toTypedSchema(
    object({
        customer: CustomerInformationCheckoutFormSchema(t),
        address: BillingAddressSchema(t),
        recurring: RecurringInformationSchema(t),
        payment: PaymentInformationSchema(t)
    })
)

const activeFacility = useActiveFacility()
const {
    handleSubmit,
    errors,
    validateField,
    validate,
    setErrors,
    setFieldError,
    values
} = useForm({
    validationSchema: schema,
    initialValues: {
        customer: CustomerInformationCheckoutFormInitialValues(
            primaryCustomer.value,
            facility.value,
            activeFacility.value.organization.onlineGuestId
        ),
        address: BillingAddressInitialValues(
            primaryCustomer.value,
            fullAddressRequired.value
        ),
        recurring: RecurringInformationInitialValues(
            requiresRecurringToken.value ? recurringTotal.value : null
        ),
        payment: PaymentInformationInitialValues(dueToday.value)
    }
})

const showPleaseFixMessage = computed(() => {
    // No submission yet means don't show the error message
    if (submitCount.value < 1) {
        return false
    }

    // If we are on the pay now portion, show all errors
    if (readyForPayment.value) {
        return Object.keys(errors.value).length > 0
    }

    // Otherwise show errors with payment filtered out
    return (
        Object.keys(errors.value).filter(field => !field.startsWith('payment'))
            .length > 0
    )
})

const pendingCustomerInformation = ref(requiresCustomerInformation.value)
const readyForRecurringToken = computed(
    () => pendingCustomerInformation.value === false
)
watch(editingCustomerInformation, (newValue, oldValue) => {
    // If we require customer information for the payment methods, when the
    // customer information changes, we have to make a new session, so reset the form
    if (requiresCustomerInformation.value) {
        if (oldValue === false && newValue === true) {
            pendingCustomerInformation.value = true
        }
    }
})
watch(editingBillingAddress, (newValue, oldValue) => {
    // If we require customer information for the payment methods, when the
    // customer information changes we have to make a new session, so reset the form
    if (requiresCustomerInformation.value) {
        if (oldValue === false && newValue === true) {
            pendingCustomerInformation.value = true
        }
    }
})

const { customer, address, paymentMethodInput } = usePaymentMethodInput()
onMounted(() => {
    if (requiresCustomerInformation.value) {
        // Only skip confirmation when neither child section has information left to collect.
        if (
            customer.value.email !== '' &&
            address.value.postal !== '' &&
            !editingCustomerInformation.value &&
            !editingBillingAddress.value
        ) {
            pendingCustomerInformation.value = false
        }
    }
})

const pendingRecurringToken = ref(requiresRecurringToken.value)
const readyForPayment = computed(
    () =>
        pendingCustomerInformation.value === false &&
        pendingRecurringToken.value === false
)
const requiresPayment = computed(() => dueToday.value.isNotZero())
const isValidating = ref(false)
// We use our own submit count because the continue to payment button does not submit the vee validate form
const submitCount = ref(0)
const paymentTitle = computed(() => {
    if (requiresRecurringToken.value) {
        return t('checkout.paymentDueToday')
    } else {
        return t('payment')
    }
})

// When editing the recurring information section, reset the form value
watch(editingRecurringInformation, (newValue, oldValue) => {
    if (oldValue === false && newValue === true) {
        pendingRecurringToken.value = true
    }
})

// References to components allowing us to submit them
const recurringInformationRef = ref<InstanceType<typeof RecurringInformation>>()
const paymentInformationRef = ref<InstanceType<typeof PaymentInformation>>()
const preservedWindcaveSessionPaymentMethod =
    ref<PaymentInformationSchemaType['paymentMethod']>(null)

// Main submit function
async function handleSubmitCheckoutForm(event?: SubmitEvent) {
    // Prevent form action
    event?.preventDefault()
    submitCount.value += 1

    // Lock every checkout control before validation because validation and payment
    // preflight yield long enough for edits to invalidate the active payment section.
    incrementProcessing()
    isValidating.value = true
    try {
        // Just get the customer information since the payment processor wants
        // this up front
        if (pendingCustomerInformation.value) {
            // Otherwise, validate the entire form
            const result = await validate()
            const isValid = Object.entries(result.results).every(
                ([key, value]) => {
                    return (
                        key.startsWith('payment') ||
                        key.startsWith('recurring') ||
                        value.valid
                    )
                }
            )

            // Also clear this error so we don't flash red on the payment error
            resetPaymentErrors(setErrors)
            // TODO: Do we need to do this? Also clear this error so we don't flash red on the recurring error
            setFieldError('recurring', undefined)

            // Ready for recurring token or payment!
            if (isValid) {
                submitCount.value = 0 // reset submit count
                editingCustomerInformation.value = false
                editingBillingAddress.value = false
                pendingCustomerInformation.value = false

                // Recurring payment method required?
                if (requiresRecurringToken.value) {
                    pendingRecurringToken.value = true
                    editingRecurringInformation.value = true
                }
            } else {
                // TODO: Find a way to scroll up
            }

            return
        }

        // Are we collecting recurring information?
        // Recurring checkout
        if (pendingRecurringToken.value) {
            // If recurring field is not valid, submit stripe form
            // We use silent so we don't show the error message until after stripe form is submitted
            const { valid: validRecurring } = await validateField('recurring', {
                mode: 'silent'
            })
            if (!validRecurring) {
                await recurringInformationRef.value.submitRecurringForm()
            }

            // Otherwise, validate the entire form
            const result = await validate()
            const isValid = Object.entries(result.results).every(
                ([key, value]) => {
                    return key.startsWith('payment') || value.valid
                }
            )

            // Also clear this error so we don't flash red on the payment error
            resetPaymentErrors(setErrors)

            if (isValid) {
                // We don't submit the form here for $0 transactions because we go
                // to the document section next

                // Close all the other pieces and we are ready for payment
                submitCount.value = 0 // reset submit count
                editingCustomerInformation.value = false
                editingBillingAddress.value = false
                pendingRecurringToken.value = false
                editingRecurringInformation.value = false
            } else {
                // TODO: Find a way to scroll up
            }

            return
        }

        // Otherwise, validate the entire form
        const result = await validate()
        const isValid = Object.entries(result.results).every(([key, value]) => {
            return key.startsWith('payment') || value.valid
        })

        if (isValid) {
            // A completed Windcave session must not be authorized again when
            // document or form validation sends the kiosk checkout through another submit.
            const selectedPaymentMethod = values.payment.paymentMethod
            const hasPreservedWindcaveSession =
                preservedWindcaveSessionPaymentMethod.value !== null &&
                selectedPaymentMethod?.type === 'PaymentMethod' &&
                selectedPaymentMethod.token ===
                    preservedWindcaveSessionPaymentMethod.value.token

            if (
                dueToday.value.isNotZero() &&
                !result.results.payment?.valid &&
                !hasPreservedWindcaveSession
            ) {
                // Also clear this error so we don't flash red on the payment error
                // onsubmit will validate the whole form again
                resetPaymentErrors(setErrors)

                const submittingWindcavePayment =
                    paymentInformationRef.value.shouldPreflightBeforeSubmittingPaymentForm()
                const paymentSubmissionResult =
                    await paymentInformationRef.value.submitPaymentForm(
                        submittingWindcavePayment
                            ? async () => {
                                  const windcavePurchaseSession =
                                      paymentInformationRef.value.getWindcavePurchaseSession()
                                  if (windcavePurchaseSession === null) {
                                      showAlert(
                                          t('headsUp'),
                                          t(
                                              'checkoutSections.unableToLoadPaymentForm'
                                          )
                                      )
                                      return false
                                  }

                                  // Hosted fields has no token before submission, so describe the
                                  // exact intended item with its checkout-owned session reference.
                                  const payment = checkoutPaymentInput(
                                      values.payment
                                  )
                                  payment.items.push({
                                      paymentSourceId:
                                          windcavePurchaseSession.paymentSourceId,
                                      amount: windcavePurchaseSession.amount,
                                      token:
                                          'WC|' +
                                          windcavePurchaseSession.sessionId
                                  })

                                  const customer = {
                                      ...values.customer,
                                      dateOfBirth: null,
                                      mobilePhone:
                                          values.customer.mobilePhone.number,
                                      address: {
                                          ...values.address,
                                          fullAddress: undefined
                                      }
                                  }
                                  if (values.customer.dateOfBirth !== '') {
                                      customer.dateOfBirth =
                                          values.customer.dateOfBirth
                                  }

                                  const recurring = requiresRecurringToken.value
                                      ? checkoutRecurringInput(values.recurring)
                                      : null

                                  return await validateInlineCheckout(
                                      {
                                          checkoutId: checkoutId.value,
                                          customer,
                                          recurring,
                                          payment,
                                          document: savedDocumentInput.value
                                      },
                                      windcavePurchaseSession.sessionId
                                  )
                              }
                            : undefined
                    )

                if (submittingWindcavePayment) {
                    if (
                        paymentSubmissionResult ===
                        PaymentSubmissionResult.CHECKOUT_STATUS_UNCERTAIN
                    ) {
                        // Reload the durable ARMED checkout so provider reconciliation replaces this stale form.
                        emit('checkoutStatusChanged')
                        return
                    }

                    if (
                        paymentSubmissionResult !==
                        PaymentSubmissionResult.SUBMITTED
                    ) {
                        // A competing tab may already own the durable attempt.
                        return
                    }

                    // Wait for the emitted Windcave token to reach vee-validate
                    // before retaining the exact session that was already authorized.
                    await nextTick()
                    const submittedPaymentMethod = values.payment.paymentMethod
                    if (
                        submittedPaymentMethod?.type === 'PaymentMethod' &&
                        submittedPaymentMethod.token?.startsWith('WC|') === true
                    ) {
                        preservedWindcaveSessionPaymentMethod.value = {
                            ...submittedPaymentMethod
                        }
                    }
                }
            }

            // Await vee-validate so the mutation callback acquires its processing
            // lock before this handler releases the validation lock in finally.
            await onSubmit(event)
            return
        } else {
            // TODO: Find a way to scroll up
        }
    } finally {
        // The successful submit callback owns a second lock until the mutation settles,
        // while validation failures and early returns release the only lock here.
        decrementProcessing()
        isValidating.value = false
    }
}

const invalidInitials = ref(new Set<number>())
const invalidSignatures = ref(new Set<number>())

const rpQuery = useRpQuery()
const queryClient = useQueryClient()
const selectedLanguage = useSelectedLanguage()
const { mutateAsync: validateInlineCheckoutMutateAsync } = useMutation({
    mutationFn: async (variables: {
        input: CommitInlineCheckoutInput
        windcaveSessionId: string
    }) => {
        const response = await rpQuery(
            graphql(/** GraphQL */ `
                mutation ValidateInlineCheckoutCustomer(
                    $input: CommitInlineCheckoutInput!
                    $windcaveSessionId: String!
                ) {
                    validateInlineCheckout(
                        input: $input
                        windcaveSessionId: $windcaveSessionId
                    ) {
                        __typename
                        ... on ValidateInlineCheckoutResult {
                            value
                        }
                        ... on Checkout {
                            status
                        }
                    }
                }
            `),
            variables
        )

        return response.validateInlineCheckout
    }
})

async function validateInlineCheckout(
    input: CommitInlineCheckoutInput,
    windcaveSessionId: string
): Promise<boolean> {
    try {
        const result = await validateInlineCheckoutMutateAsync({
            input,
            windcaveSessionId
        })
        if (result.__typename === 'ValidateInlineCheckoutResult') {
            return result.value
        }

        // The winner already armed or advanced this checkout; render its durable state.
        emit('checkoutStatusChanged')
        return false
    } catch (error) {
        if (
            !(error instanceof UserReportableError) &&
            !(error instanceof GraphQLRequestError)
        ) {
            showAlert(t('headsUp'), String(error))
        }
        return false
    }
}

const { mutate, isPending } = useMutation({
    mutationFn: async (mutationData: CommitInlineCheckoutInput) => {
        const response = await rpQuery(
            graphql(/** GraphQL */ `
                mutation CommitInlineCheckoutCustomer(
                    $input: CommitInlineCheckoutInput!
                    $language: Language!
                ) {
                    commitInlineCheckout(input: $input) {
                        __typename
                        ... on CommitCheckoutResult {
                            ...CheckoutSuccessKioskDisplayFragment
                        }
                        ... on CheckoutProcessing {
                            checkoutId
                        }
                        ... on ValidationError {
                            fieldErrors {
                                path
                                message
                            }
                        }
                    }
                }
            `),
            {
                input: mutationData,
                language: selectedLanguage.value
            }
        )

        if (response.commitInlineCheckout === null) {
            throw new Error(
                t('checkout.inlineCheckoutError', {
                    code: 'CFKC'
                })
            )
        }

        if (
            response.commitInlineCheckout.__typename ===
                'CommitCheckoutResult' ||
            response.commitInlineCheckout.__typename === 'CheckoutProcessing' ||
            response.commitInlineCheckout.__typename === 'ValidationError'
        ) {
            return response.commitInlineCheckout
        }

        throw new Error()
    }
})

// Once form is validated, this preps and calls the mutation
const showAlert = useShowAlert()
const savedDocumentInput = ref()
const onSubmit = handleSubmit(async values => {
    // Customer and Address
    const customer = {
        ...values.customer,
        dateOfBirth: null,
        mobilePhone: values.customer.mobilePhone.number,
        address: {
            ...values.address,
            // Wipe out the full address
            fullAddress: undefined
        }
    }

    // Add date of birth if it exists
    if (values.customer.dateOfBirth !== '') {
        customer.dateOfBirth = values.customer.dateOfBirth
    }

    // Recurring Token
    let recurring: CheckoutRecurringInput | null = null
    if (requiresRecurringToken.value) {
        recurring = checkoutRecurringInput(values.recurring)
    }

    // Payment Items
    const payment = checkoutPaymentInput(values.payment)

    incrementProcessing()

    // Reuse the submitted payment input for Windcave error recovery below.
    const mutationData: CommitInlineCheckoutInput = {
        checkoutId: checkoutId.value,
        customer,
        recurring,
        payment,
        document: savedDocumentInput.value
    }

    mutate(mutationData, {
        onSuccess: data => {
            if (data.__typename === 'ValidationError') {
                handleValidationErrors(
                    data.fieldErrors,
                    invalidInitials,
                    invalidSignatures
                )

                let messageBuilder = ''
                data.fieldErrors.forEach(error => {
                    if (
                        error.path !== 'initials' &&
                        error.path !== 'signatures'
                    ) {
                        messageBuilder += error.message
                    }
                })
                if (messageBuilder !== '') {
                    showAlert(t('headsUp'), messageBuilder)
                }
            } else if (data.__typename === 'CheckoutProcessing') {
                // The embedded checkout query owns processing polling and durable result rendering.
                emit('checkoutStatusChanged')
            } else {
                emit('kioskCheckoutSuccess', data)
            }
        },
        onError(error) {
            if (
                !(error instanceof UserReportableError) &&
                !(error instanceof GraphQLRequestError)
            ) {
                showAlert(t('headsUp'), String(error))
            }

            if (
                mutationData.payment.items.some(paymentItem =>
                    paymentItem.token.startsWith('WC|')
                )
            ) {
                // A lost response may hide ARMED, PROCESSING, or completion. Refetch durable state
                // before requesting another hosted session from a form that may need to unmount.
                preservedWindcaveSessionPaymentMethod.value = null
                void queryClient
                    .invalidateQueries({
                        predicate: query =>
                            query.queryKey.includes('InlineCheckoutQuery')
                    })
                    .then(() => {
                        // Only a confirmed DRAFT checkout still renders this form and needs a new session.
                        paymentInformationRef.value?.retryWindcavePayment()
                    })
            }
        },
        onSettled() {
            decrementProcessing()
        }
    })
})

function handleInlineCheckoutDocument(documentInput: DocumentInput) {
    savedDocumentInput.value = documentInput
}

function handleInlineCheckout(documentInput: DocumentInput) {
    savedDocumentInput.value = documentInput
    handleSubmitCheckoutForm().catch(error => {
        showAlert(t('headsUp'), String(error))
    })
}
</script>
